/**
 * Standard format for export files. Each tool produces a JSON file
 * in this shape; Build your binder parses one or more files and merges sections.
 *
 * Schema:
 * - version: number (e.g. 1)
 * - toolId: string (e.g. "emergency-plan", "budget")
 * - toolName: string (display name, e.g. "Emergency Plan")
 * - exportedAt: string (ISO date)
 * - sections: Array<{ name: string, content: string }>  (content is HTML)
 */

export const BINDER_EXPORT_VERSION = 1;
export const BINDER_EXPORT_EXTENSION = '.binder.json';

/**
 * Build a binder export object for a tool (call before JSON.stringify and download).
 * @param {string} toolId
 * @param {string} toolName
 * @param {Array<{ name: string, content: string }>} sections
 * @returns {object} Ready to JSON.stringify
 */
export function buildBinderExport(toolId, toolName, sections) {
  return {
    version: BINDER_EXPORT_VERSION,
    toolId,
    toolName,
    exportedAt: new Date().toISOString(),
    sections: Array.isArray(sections) ? sections : [],
  };
}

/**
 * Parse and validate an uploaded file. Returns normalized { toolId, toolName, sections } or throws.
 * @param {string} rawText - File content (e.g. from FileReader)
 * @param {string} [filename] - Optional, for error messages
 * @returns {{ toolId: string, toolName: string, sections: Array<{ name: string, content: string }> }}
 */
export function parseBinderExportFile(rawText, filename = '') {
  let data;
  try {
    data = JSON.parse(rawText);
  } catch (e) {
    throw new Error(`Invalid JSON in ${filename || 'file'}: ${e.message}`);
  }
  if (data == null || typeof data !== 'object') {
    throw new Error(`Invalid binder export: expected an object`);
  }
  const version = data.version;
  if (version == null || typeof version !== 'number') {
    throw new Error(`Invalid binder export: missing or invalid "version"`);
  }
  const toolId = data.toolId;
  const toolName = data.toolName;
  if (typeof toolId !== 'string' || !toolId.trim()) {
    throw new Error(`Invalid binder export: missing or invalid "toolId"`);
  }
  if (typeof toolName !== 'string' || !toolName.trim()) {
    throw new Error(`Invalid binder export: missing or invalid "toolName"`);
  }
  const rawSections = data.sections;
  if (!Array.isArray(rawSections)) {
    throw new Error(`Invalid binder export: "sections" must be an array`);
  }
  const sections = rawSections
    .filter((s) => s != null && typeof s.name === 'string' && typeof s.content === 'string')
    .map((s) => ({ name: s.name.trim() || 'Untitled', content: s.content }));
  return { toolId: toolId.trim(), toolName: toolName.trim(), sections };
}
